import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, statusClass, statusLabel } from '../lib/format.ts';

interface CustomerDetail {
  id: number;
  customerNo: string;
  name: string;
  currency: string;
  country: string | null;
  salesman: string | null;
  balance: number;
  balanceStr: string | null;
  hasDeliveryAddresses: boolean;
  termText: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  deliveryAddresses: Array<{
    id: number;
    deliverTo: string | null;
    shipAddr1: string | null;
    shipAddr2: string | null;
    shipAddr3: string | null;
    shipAddr4: string | null;
    shipAddr5: string | null;
  }>;
  recentInvoices: Array<{
    invoiceNo: string;
    invoiceDate: string;
    value: number;
    status: string;
    outstanding: number | null;
  }>;
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: () =>
      apiFetch<CustomerDetail>(`/api/dashboard/customers/${encodeURIComponent(id ?? '')}`),
    enabled: !!id,
  });

  return (
    <>
      <Link to="/customers" className="back-link">← All customers</Link>

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">{data.name}</h1>
              <span className="main-subtitle mono">{data.customerNo}</span>
            </div>
          </header>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Master record</h2>
            </header>
            <dl className="detail-grid">
              <dt>Customer code</dt>
              <dd className="mono">{data.customerNo}</dd>
              <dt>Currency</dt>
              <dd>{data.currency}</dd>
              <dt>Country</dt>
              <dd>{data.country || '—'}</dd>
              <dt>Salesman</dt>
              <dd>{data.salesman || '—'}</dd>
              <dt>Balance</dt>
              <dd>{data.balanceStr || money(data.balance, data.currency)}</dd>
              <dt>Payment terms</dt>
              <dd>
                {data.termText || (
                  <em style={{ color: 'var(--text-dim)' }}>(detail-fetch pass pending)</em>
                )}
              </dd>
              <dt>Phone</dt>
              <dd className="mono">{data.contactPhone || '—'}</dd>
              <dt>Email</dt>
              <dd>{data.contactEmail || '—'}</dd>
            </dl>
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Delivery addresses</h2>
              <span className="panel-subtitle">{data.deliveryAddresses.length} on file</span>
            </header>
            {data.deliveryAddresses.length === 0 ? (
              <div className="empty">
                {data.hasDeliveryAddresses
                  ? 'List endpoint says addresses exist; detail-fetch pass will populate them here.'
                  : 'No delivery addresses on file.'}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {data.deliveryAddresses.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      padding: '0.75rem 1rem',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border-soft)',
                    }}
                  >
                    {a.deliverTo && <div style={{ fontWeight: 600 }}>{a.deliverTo}</div>}
                    {[a.shipAddr1, a.shipAddr2, a.shipAddr3, a.shipAddr4, a.shipAddr5]
                      .filter((s) => s && s.trim())
                      .map((s, i) => (
                        <div key={i} style={{ color: 'var(--text-muted)' }}>
                          {s}
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Recent invoices</h2>
              <span className="panel-subtitle">{data.recentInvoices.length} most recent</span>
            </header>
            {data.recentInvoices.length === 0 ? (
              <div className="empty">No invoices on file.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th className="num">Value</th>
                    <th className="num">Outstanding</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentInvoices.map((i) => (
                    <tr key={i.invoiceNo}>
                      <td className="mono">
                        <Link to={`/invoices/${encodeURIComponent(i.invoiceNo)}`}>
                          {i.invoiceNo}
                        </Link>
                      </td>
                      <td className="mono">{dateOnly(i.invoiceDate)}</td>
                      <td className="num">{money(i.value, data.currency)}</td>
                      <td className="num">{money(i.outstanding, data.currency)}</td>
                      <td>
                        <span className={`badge ${statusClass(i.status)}`}>
                          {statusLabel(i.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </>
  );
}
